/* eslint-disable */
import { AddCircle, ArrowDropDown, Close } from '@mui/icons-material';
import { ClickAwayListener } from '@mui/material';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { AutoSizer, List, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import { classes, convertWeightUnit } from '../../Helpers';
import styles from './CustomInputSelector.module.scss';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/input';

const renderVariantSelector = (
	ele,
	addedItems,
	handleVariantSelection,
	skuStockInfo,
	packSizedOrder = false,
	unpackedOption = false
) => {
	return (
		<div className={styles.variants}>
			{ele?.variants?.map((variant) => {
				let alreadyAdded = false;
				if (packSizedOrder) {
					// For pack-sized orders, check if ALL pack sizes for this variant have been added
					const packSizeConfig = variant?.packSizeConfig || [];
					if (packSizeConfig.length === 0) {
						// No pack sizes configured, treat as normal variant
						alreadyAdded = addedItems
							?.some((poItem) => poItem?.selectedVariant?.id === variant?.id);
					} else {
						// Check if all pack sizes have been added
						// Get all added pack size IDs for this variant
						const addedPackSizeIds = addedItems
							?.filter((poItem) => poItem?.selectedVariant?.id === variant?.id)
							?.map((poItem) => poItem?.selectedPackSize?.id)
							?.filter(Boolean) || [];

						// When unpackedOption is true, also check if 'unpacked' option has been used
						if (unpackedOption) {
							// All options include pack sizes + unpacked option
							const allPackIds = [...packSizeConfig.map((pack) => pack.id), 'unpacked'];
							// Check if every pack size (including unpacked) has been used
							alreadyAdded = allPackIds.every((packId) => addedPackSizeIds.includes(packId));
						} else {
							// Check if every pack size has been used
							alreadyAdded = packSizeConfig.every((pack) => addedPackSizeIds.includes(pack.id));
						}
					}
				} else {
					// Normal behavior: check if variant is already in added items
					alreadyAdded = addedItems?.find((poItem) => {
						return poItem?.id === variant?.id;
					});
				}
				return (
					!alreadyAdded && (
						<div
							onClick={() => {
								handleVariantSelection(variant, ele);
							}}
							className={styles.variantInfoContainer}
						>
							<div className={styles.variant}>
								{variant?.displayName}
								{variant?.clientVariantId ? (
									<span className={styles.skuCode}>
										({variant?.clientVariantId})
									</span>
								) : ''}
							</div>
							{skuStockInfo?.map((infoEle) => {
								if (!infoEle?.showInDropDown) {
									return <></>;
								}
								return (
									<span
										className={styles?.quantities}
										style={{
											backgroundColor: infoEle?.bgColor,
											color: infoEle?.color,
										}}
									>
										<span className={styles?.title}>
											{infoEle?.name}
											:
										</span>
										<span className={styles?.value}>
											{ele?.unit === 'count' ? `${infoEle?.values?.[
												variant?.id
											] ?? 0}pc` : convertWeightUnit(
												infoEle?.values?.[
												variant?.id
												] ?? 0,
												ele?.unit,
												undefined,
												true
											)}
										</span>
									</span>
								);
							})}
						</div>
					)
				);
			})}
		</div>
	);
};

function CustomInputSelector(props) {
	const {
		inputLists,
		placeholder,
		width,
		handleSelection,
		selectedValue,
		addNewText,
		tiny = false,
		skuStockInfo,
		classname,
		handleAddNew = () => { },
		handleKeyPress = () => { },
		handleRemove = () => { },
		showImage = false,
		addedItems = [],
		hideAddNew = false,
		allowRemove = true,
		inputName = '',
		disabled = false,
		required = false,
		inputMin,
		packSizedOrder = false,
		unpackedOption = false,
	} = props;

	const [searchQuery, setSearchQuery] = useState('');
	const [showDropdown, setShowDropdown] = useState(false);
	const cache = useRef(new CellMeasurerCache({
		fixedWidth: true,
		defaultHeight: 80,
		minHeight: 50,
	}));
	const inputRef = useRef(null);
	const handleChange = (e) => {
		setSearchQuery(e.target.value);
	};
	const handleVariantSelection = (variant, product) => {
		setShowDropdown(false);
		handleSelection({
			...product,
			selectedVariant: variant,
		});
	};

	function areAllVariantsAdded(variants, objectsArray) {
		if (packSizedOrder) {
			// For pack-sized orders, check if ALL pack sizes for EVERY variant have been added
			return variants.every((variant) => {
				const packSizeConfig = variant?.packSizeConfig || [];
				if (packSizeConfig.length === 0) {
					// No pack sizes configured, check if variant is added
					return objectsArray.some((object) => object?.selectedVariant?.id === variant?.id);
				}
				// Get all added pack size IDs for this variant
				const addedPackSizeIds = objectsArray
					?.filter((poItem) => poItem?.selectedVariant?.id === variant?.id)
					?.map((poItem) => poItem?.selectedPackSize?.id)
					?.filter(Boolean) || [];

				// When unpackedOption is true, also check if 'unpacked' option has been used
				if (unpackedOption) {
					// All options include pack sizes + unpacked option
					const allPackIds = [...packSizeConfig.map((pack) => {
						return pack.id;
					}), 'unpacked'];
					// Check if every pack size (including unpacked) has been used
					return allPackIds.every((packId) => addedPackSizeIds.includes(packId));
				}
				// Check if every pack size has been used
				return packSizeConfig.every((pack) => addedPackSizeIds.includes(pack.id));
			});
		}
		// Normal behavior
		return variants.every((variant) => {
			return objectsArray.some((object) => {
				return object.id === variant?.id;
			});
		});
	}

	const hideDropdown = () => {
		setShowDropdown(false);
	};

	useEffect(() => {
		if (document.activeElement === inputRef.current) {
			setShowDropdown(true);
		}
	}, [document.activeElement]);


	const filterListInput = inputLists?.filter((ele) => {
		const query = searchQuery?.toLowerCase()?.trim();
		if (!query) return true;

		const nameMatch = (ele?.name ?? ele?.title)
			?.toLowerCase()
			?.indexOf(query) !== -1;

		const variantIdMatch = ele?.variants?.some((variant) =>
			variant?.clientVariantId != null &&
			variant?.clientVariantId?.toString()?.toLowerCase()?.indexOf(query) !== -1
		);
		const variantNameMatch = ele?.variants?.some((variant) =>
			variant?.displayName?.toLowerCase()?.indexOf(query) !== -1
		);
		return nameMatch || variantIdMatch || variantNameMatch;
	});


	useEffect(() => {
		cache.current.clearAll();
	}, [filterListInput?.length, addedItems?.length]);

	const row = ({ index, style, key, parent }) => {
		const ele = filterListInput[index];
		return (
			<CellMeasurer
				key={key}
				index={index}
				cache={cache.current}
				rowIndex={index}
				columnIndex={0}
				parent={parent}
			>
				<div
					onClick={() => {
						if (!ele?.variants?.length > 0) {
							setShowDropdown(false);
							handleSelection(ele);
						}
					}}
					style={{
						...style,
						...(!ele?.variants?.length > 0
							? {
								cursor: 'pointer',
							}
							: {}),
					}}
					className={styles['dropdown-item']}
				>
					{showImage && ele?.imageUrl && (
						<img
							src={ele?.imageUrl}
							alt='Option'
						/>
					)}
					<div
						className={styles['details-container']}
					>
						<p>{ele?.name ?? ele?.title}</p>
						{ele?.variants?.length > 0 && (
							<div>
								{!areAllVariantsAdded(
									ele?.variants,
									addedItems
								)
									? renderVariantSelector(
										ele,
										addedItems,
										handleVariantSelection,
										skuStockInfo,
										packSizedOrder,
										unpackedOption
									)
									: 'Item Already Added'}
							</div>
						)}
					</div>
				</div>
			</CellMeasurer>
		);
	};
	return (
		// <div classname={classname}>
		// 	<DropdownMenu>
		// 		<DropdownMenuTrigger>
		// 			<Input
		// 				name={inputName}
		// 				required={required}
		// 				className={styles['search-input']}
		// 				placeholder={placeholder}
		// 				value={selectedValue ?? searchQuery}
		// 				onChange={handleChange}
		// 				onKeyDown={handleKeyPress}
		// 				autoComplete='off'
		// 				min={inputMin}
		// 				onFocus={() => {
		// 					setShowDropdown(true);
		// 				}}
		// 				ref={inputRef}
		// 			/>
		// 		</DropdownMenuTrigger>
		// 		<DropdownMenuContent className='z-[9999]'>
		// 			{!hideAddNew && (
		// 				<DropdownMenuItem
		// 					className={styles['dropdown-item']}
		// 					onClick={() => {
		// 						setShowDropdown(false);
		// 						handleAddNew();
		// 					}}
		// 				>
		// 					<AddCircle className={styles.icon} />
		// 					<p>{addNewText}</p>
		// 				</DropdownMenuItem>
		// 			)}
		// 			{inputLists
		// 				?.filter((ele) => {
		// 					return (
		// 						ele?.name
		// 							?.toLowerCase()
		// 							?.indexOf(
		// 								searchQuery?.toLowerCase()?.trim()
		// 							) !== -1
		// 					);
		// 				})
		// 				?.map((ele) => {
		// 					return (
		// 						<DropdownMenuItem
		// 							onClick={() => {
		// 								if (!ele?.variants?.length > 0) {
		// 									setShowDropdown(false);
		// 									handleSelection(ele);
		// 								}
		// 							}}
		// 							style={
		// 								!ele?.variants?.length > 0
		// 									? {
		// 											cursor: 'pointer',
		// 									  }
		// 									: {}
		// 							}
		// 							className={styles['dropdown-item']}
		// 						>
		// 							{showImage && ele?.imageUrl && (
		// 								<img src={ele?.imageUrl} alt='Option' />
		// 							)}
		// 							<div
		// 								className={styles['details-container']}
		// 							>
		// 								<p>{ele?.name ?? ele?.title}</p>
		// 								{ele?.variants?.length > 0 && (
		// 									<div>
		// 										{!areAllVariantsAdded(
		// 											ele?.variants,
		// 											addedItems
		// 										)
		// 											? renderVariantSelector(
		// 													ele,
		// 													addedItems,
		// 													handleVariantSelection,
		// 													skuStockInfo
		// 											  )
		// 											: 'Item Already Added'}
		// 									</div>
		// 								)}
		// 							</div>
		// 						</DropdownMenuItem>
		// 					);
		// 				})}
		// 			{/* <DropdownMenuLabel>My Account</DropdownMenuLabel> */}
		// 			{/* <DropdownMenuItem>Profile</DropdownMenuItem>
		// 			<DropdownMenuItem>Billing</DropdownMenuItem>
		// 			<DropdownMenuItem>Team</DropdownMenuItem>
		// 			<DropdownMenuItem>Subscription</DropdownMenuItem> */}
		// 		</DropdownMenuContent>
		// 	</DropdownMenu>
		// </div>
		<ClickAwayListener onClickAway={hideDropdown}>
			<div
				style={{
					width,
				}}
				className={classes(styles.root, classname)}
			>
				<div className={styles.input}>
					{disabled && <div className={styles.disabled} />}
					<input
						name={inputName}
						required={required}
						className={tiny ? styles['search-input-tiny'] : styles['search-input']}
						placeholder={placeholder}
						value={selectedValue ?? searchQuery}
						onChange={handleChange}
						onKeyDown={handleKeyPress}
						autoComplete='off'
						min={inputMin}
						onFocus={() => {
							setShowDropdown(true);
						}}
						ref={inputRef}
					/>
					<div className={styles['icon-conainer']}>
						{selectedValue && allowRemove && (
							<div
								onClick={() => {
									handleRemove(inputName);
								}}
								className={styles.remove}
							>
								<Close />
							</div>
						)}
						<ArrowDropDown />
					</div>
				</div>
				{!disabled && showDropdown && (
					<div
						className={styles['query-items']}
					>
						{!hideAddNew && (
							<div
								className={styles['dropdown-item']}
								onClick={() => {
									setShowDropdown(false);
									handleAddNew();
								}}
							>
								<AddCircle className={styles.icon} />
								<p>{addNewText}</p>
							</div>
						)}
						{!filterListInput?.length > 0 ? (
							<p className={styles.empty}>Empty List</p>
						)
							: (
								<AutoSizer>
									{({ width, height }) => (
										<div style={{
											background: 'white',
											height,
											width,
											boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
										}}
										>
											<List
												height={height}
												width={width}
												rowCount={filterListInput.length}
												rowHeight={cache.current.rowHeight}
												deferredMeasurementCache={cache.current}
												rowRenderer={row}
											/>
										</div>
									)}
								</AutoSizer>
							)}
					</div>
				)}
			</div>
		</ClickAwayListener>
	);
}

export default CustomInputSelector;
